
import { defineComponent, onMounted, ref, watchEffect, computed, inject } from 'vue';
import { useRoute } from 'vue-router';
import { useRouter } from 'vue-router';
import { Apollo, Axios, Notify } from '@/core/services';
import { useStore } from 'vuex';
import { ORDER_SUBSCRIPTION_DETAILS } from '@/modules/subscription/graphql/Queries';
import { useI18n } from 'vue-i18n';
import { Actions } from '@/store/enums/StoreEnums';
import SubscriptionProducts from './SubscriptionProducts.vue';
import Table from '../../../components/Table/Table.vue';
import { DELETE_SUBSCRIPTION_PRODUCT, UPDATE_SUBSCRIPTION_PRODUCT, UPDATE_SUBSCRIPTION_PAYMENT_METHOD } from '../graphql/Mutations';
// import InnerLoader from "../../../components/InnerLoader.vue";
import SubscriptionLoader from './SubscriptionLoader.vue';
import FrequencyChanging from '../components/FrequencyChanging.vue';
import { GET_PAYMENT_METHODS } from '@/modules/system/payment-methods/graphql/Queries';
export default defineComponent({
    name: 'Subscription',
    components: {
        SubscriptionProducts,
        Table,
        // InnerLoader,
        SubscriptionLoader,
        FrequencyChanging
    },
    setup() {
        const response = ref({}) as Record<any, any>;
        const frequencyModal = ref<null | HTMLFormElement>(null);
        const router = useRouter();
        const route = useRoute();
        const storeHandler = useStore();
        const emitter: any = inject('emitter');
        const i18n = useI18n();
        const subscription_details = ref({}) as Record<any, any>;
        const order_products = ref({}) as Record<any, any>;
        const price_tax = ref(0);
        const system_locale = ref();
        const order_subscription_history = ref({}) as Record<any, any>;
        const loader = ref(false);
        const productLists = ref<null | HTMLFormElement>(null);
        const cartLimits = ref([]) as Record<any, any>;
        const paymentMethods = ref([] as any[]);
        const paymentMethodsLoading = ref(false);
        const selectedPaymentMethodId = ref(null as number | null);
        const previousPaymentMethodId = ref(null as number | null);
        const customerEanInfo = ref(null as any);
        const paymentSaving = ref(false);
        const columns = ref([
            {
                label: '',
                key: 'image'
            },
            {
                label: 'message.PRODUCT_NAME',
                key: 'name'
            },
            {
                label: 'message.PRICE',
                key: 'price',
                textAlignment: 'end'
            },
            {
                label: 'message.QUANTITY',
                key: 'quantity'
            },
            {
                label: 'message.TOTAL',
                key: 'total',
                textAlignment: 'end'
            },
            {
                label: 'message.ACTIONS',
                key: 'actions',
                textAlignment: 'center'
            }
        ]);
        const columns_order = ref([
            {
                label: 'message.ORDER_ID',
                key: 'id'
            },
            {
                label: 'message.ORDER_DATE',
                key: 'order_date'
            },
            {
                label: 'message.STATUS',
                key: 'status'
            },
            {
                label: 'message.ECONOMIC',
                key: 'economic'
            },
            {
                label: 'message.SHIPMENT_STATUS',
                key: 'shipment_status'
            },
            {
                label: 'message.ACTIONS',
                key: 'actions',
                textAlignment: 'center'
            }
        ]);

        const subscribeData = computed(() => {
            return storeHandler.getters?.getAllSubscription;
        });

        const customerAddress = computed(() => {
            const customer = subscribeData.value?.order_subscription?.customer;
            if (customer?.addresses && Array.isArray(customer.addresses)) {
                const defaultPaymentAddress = customer.addresses.find((addr: any) => addr?.default_payment_address === 1);
                if (defaultPaymentAddress?.address_1) {
                    return defaultPaymentAddress.address_1;
                }
            }
            return customer?.address || '';
        });

        const resolvePaymentMethodName = (pm: any, locale?: string) => {
            if (!pm) return '';
            const loc = locale || system_locale.value;
            try {
                const parsed = typeof pm.name === 'string' ? JSON.parse(pm.name) : pm.name;
                if (parsed && typeof parsed === 'object') {
                    const fromLocale = parsed?.[loc]?.name;
                    if (fromLocale) return fromLocale;
                    const entries = Object.values(parsed) as Array<{ name?: string }>;
                    const first = entries.find(v => v?.name)?.name;
                    if (first) return first;
                }
            } catch (e) {
                /* ignore parse errors */
            }
            return pm.name || pm.type || String(pm.id);
        };

        const isEanPaymentMethod = (pm: any) => {
            if (!pm) return false;
            const label = resolvePaymentMethodName(pm).toLowerCase();
            if (label.includes('ean')) return true;
            try {
                const parsed = typeof pm.name === 'string' ? JSON.parse(pm.name) : pm.name;
                if (parsed && typeof parsed === 'object') {
                    return (Object.values(parsed) as Array<{ name?: string }>).some(v =>
                        String(v?.name || '')
                            .toLowerCase()
                            .includes('ean')
                    );
                }
            } catch (e) {
                /* ignore */
            }
            return String(pm.name || '')
                .toLowerCase()
                .includes('ean');
        };

        const paymentMethodOptions = computed(() => {
            const locale = system_locale.value;
            return (paymentMethods.value || [])
                .filter((pm: any) => pm?.status == 1)
                .map((pm: any) => ({
                    value: pm.id,
                    label: resolvePaymentMethodName(pm, locale),
                    type: pm.type,
                    raw: pm
                }));
        });

        const normalizeCustomerEanInfo = (info: any) => {
            if (!info || typeof info !== 'object') return null;
            const ean = info.ean;
            if (ean === null || ean === undefined || ean === '') return null;
            // Backend update_subscription_payment_method requires status === 200 (Economic shop response shape)
            const status = info.status != null ? Number(info.status) : 200;
            return {
                status: status || 200,
                ean,
                customer_contact_info: {
                    name: info.customer_contact_info?.name || info.contact_person_name || '',
                    number: info.customer_contact_info?.number || info.contact_person_phone || '',
                    sortKey: info.customer_contact_info?.sortKey || info.sortKey || ''
                },
                contact_person_name: info.contact_person_name || info.customer_contact_info?.name || '',
                contact_person_phone: info.contact_person_phone || info.customer_contact_info?.number || '',
                sortKey: info.sortKey || info.customer_contact_info?.sortKey || ''
            };
        };

        const showEanInfo = computed(() => {
            const selected = paymentMethods.value.find((pm: any) => pm.id === selectedPaymentMethodId.value);
            return isEanPaymentMethod(selected) && !!customerEanInfo.value?.ean;
        });

        const displayEanNumber = computed(() => customerEanInfo.value?.ean || '');

        const displayEanContactPerson = computed(() => customerEanInfo.value?.customer_contact_info?.name || customerEanInfo.value?.contact_person_name || '');

        const syncPaymentSelectionFromSubscription = () => {
            const sub = response.value?.order_subscription || subscribeData.value?.order_subscription;
            if (!sub) return;
            const id = sub.payment_id != null ? Number(sub.payment_id) : null;
            selectedPaymentMethodId.value = id;
            previousPaymentMethodId.value = id;

            // Prefer EAN payload returned by order_subscription_details (customer_ean_info)
            const fromBackend = normalizeCustomerEanInfo(sub.customer_ean_info);
            if (fromBackend) {
                customerEanInfo.value = fromBackend;
            } else {
                const selected = paymentMethods.value.find((pm: any) => pm.id === id);
                if (!isEanPaymentMethod(selected)) {
                    customerEanInfo.value = null;
                }
            }
        };

        const subscriptionDetails = uuid => {
            loader.value = true;
            Apollo.watchQuery({
                query: ORDER_SUBSCRIPTION_DETAILS,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'cache-only',
                variables: {
                    uuid: uuid
                }
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                }
                loader.value = false;
                response.value = JSON.parse(data.order_subscription_details);

                storeHandler.dispatch(Actions.SET_SUBSCRIPTION, response);
                syncPaymentSelectionFromSubscription();
            });
        };

        const loadPaymentMethods = () => {
            paymentMethodsLoading.value = true;
            const sub = Apollo.watchQuery({
                query: GET_PAYMENT_METHODS,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'cache-only'
            }).subscribe(({ data, errors }) => {
                if (!errors) {
                    paymentMethods.value = Array.isArray(data?.payment_methods) ? data.payment_methods : [];
                    syncPaymentSelectionFromSubscription();
                }
                paymentMethodsLoading.value = false;
                sub.unsubscribe();
            });
        };

        const fetchEconomicCustomerInfo = async () => {
            const customer = subscribeData.value?.order_subscription?.customer;
            const formData = {
                economic_number: customer?.economic_number || '',
                country_code: customer?.sik || ''
            };

            if (!formData.economic_number || !formData.country_code) {
                Notify.info(i18n.t('message.EAN_NUMBER_NOT_EXIST'), { html: true });
                return null;
            }
            try {
                const apiResponse = await Axios.post('/economic/get-customer-shop', formData);
                if (apiResponse?.data?.status == 404) {
                    Notify.info(i18n.t('message.EAN_NUMBER_NOT_EXIST'), { html: true });
                    return null;
                }
                // Keep full Economic payload (status + ean + customer_contact_info) for the mutation
                const normalized = normalizeCustomerEanInfo(apiResponse?.data);
                return normalized || apiResponse?.data || null;
            } catch (e) {
                Notify.error(i18n.t('message.ERROR'));
                return null;
            }
        };

        const revertPaymentSelection = () => {
            selectedPaymentMethodId.value = previousPaymentMethodId.value;
        };

        const saveSubscriptionPaymentMethod = async (paymentMethodId: number, eanInfo: any) => {
            const subscriptionId = subscribeData.value?.order_subscription?.id;
            if (!subscriptionId) return false;
            paymentSaving.value = true;
            try {
                await Apollo.mutate({
                    mutation: UPDATE_SUBSCRIPTION_PAYMENT_METHOD,
                    variables: {
                        subscription_id: subscriptionId,
                        payment_method_id: paymentMethodId,
                        customer_ean_info: eanInfo ? JSON.stringify(eanInfo) : null
                    }
                });
                previousPaymentMethodId.value = paymentMethodId;
                Notify.success(i18n.t('message.RECORD_UPDATED_SUCCESSFULLY'));
                subscriptionDetails(route.params.uuid);
                return true;
            } catch (e) {
                // GraphQL errors are already surfaced by ApolloService error link
                return false;
            } finally {
                paymentSaving.value = false;
            }
        };

        const onPaymentMethodChange = async (paymentMethodId: number) => {
            if (paymentSaving.value) return;
            if (paymentMethodId === previousPaymentMethodId.value) return;

            const selected = paymentMethods.value.find((pm: any) => pm.id === paymentMethodId);
            let eanInfo: any = null;

            if (isEanPaymentMethod(selected)) {
                loader.value = true;
                eanInfo = await fetchEconomicCustomerInfo();
                loader.value = false;
                if (!eanInfo || eanInfo.status == 404 || !eanInfo.ean) {
                    customerEanInfo.value = null;
                    revertPaymentSelection();
                    return;
                }
                customerEanInfo.value = eanInfo;
            } else {
                customerEanInfo.value = null;
            }

            loader.value = true;
            const ok = await saveSubscriptionPaymentMethod(paymentMethodId, eanInfo);
            loader.value = false;
            if (!ok) {
                revertPaymentSelection();
                customerEanInfo.value = null;
            }
        };

        const showFrequencyModal = () => {
            frequencyModal.value?.show(response.value);
        };

        watchEffect(() => {
            system_locale.value = i18n.fallbackLocale.value;
            emitter.emit('setPageHeader', response.value?.order_subscription?.order_discounts?.discount?.discount_type);
        });

        const handleFrequencyUpdated = (success: boolean) => {
            if (success) {
                // Re-fetch your subscription details
                subscriptionDetails(route.params.uuid);
            }
        };

        onMounted(async () => {
            loadPaymentMethods();
            await subscriptionDetails(route.params.uuid);
        });
        const handleSubscriptionDelete = subscription => {
            storeHandler.getters.appInstance.$messageBox
                .confirm(`${i18n.t('message.ARE_YOU_SURE_TO_DELETE_RECORD')}?`, i18n.t('message.INFO'), {
                    confirmButtonText: i18n.t('message.OK'),
                    cancelButtonText: i18n.t('message.CANCEL'),
                    type: 'info'
                })
                .then(async () => {
                    loader.value = true;
                    await Apollo.mutate({
                        mutation: DELETE_SUBSCRIPTION_PRODUCT,
                        variables: {
                            order_subscription_id: subscription.order_subscription_id,
                            product_id: subscription.product_id
                        },
                        update: (store, { data: { delete_subscription_product } }) => {
                            const response = JSON.parse(delete_subscription_product);
                            storeHandler.dispatch(Actions.SET_SUBSCRIPTION, response);
                            loader.value = false;
                        }
                    });
                    Notify.success(i18n.t('message.SUBSCRIPTION_PRODUCT_DELETE_SUCCESSFULLY'));
                })
                .catch(() => {
                    loader.value = false;
                });
        };

        const cartLimit = (multipleLimit, multiple) => {
            cartLimits.value = [];
            const limit = multipleLimit / multiple;
            let res = 0;
            for (let i = 1; i <= limit; i++) {
                res = multiple + res;
                cartLimits.value.push(res);
            }
        };

        const handleChange = async (quantity, product_id, order_subscription_id) => {
            try {
                loader.value = true;
                await Apollo.mutate({
                    mutation: UPDATE_SUBSCRIPTION_PRODUCT,
                    variables: {
                        product_id: product_id,
                        quantity: quantity,
                        multiple: true,
                        order_subscription_id: order_subscription_id
                    },
                    update: (store, { data: { update_subscription_product } }) => {
                        const response = JSON.parse(update_subscription_product);
                        storeHandler.dispatch(Actions.SET_SUBSCRIPTION, response);
                        Notify.success(i18n.t('message.SUBSCRIPTION_PRODUCT_UPDATED_SUCCESSFULLY'));
                        loader.value = false;
                    }
                });
            } catch ($e) {
                loader.value = false;
            }
        };

        emitter.on('openSubscriptionProductModel', () => {
            productLists.value?.show(subscribeData.value?.order_subscription?.id);
        });

        const handleView = (order: Record<any, any>) => {
            router.push({
                name: 'order_details',
                params: { uuid: order?.current_order?.uuid }
            });
        };

        return {
            subscription_details,
            order_subscription_history,
            order_products,
            price_tax,
            system_locale,
            subscribeData,
            customerAddress,
            productLists,
            loader,
            cartLimits,
            columns,
            columns_order,
            handleView,
            handleSubscriptionDelete,
            cartLimit,
            handleChange,
            frequencyModal,
            showFrequencyModal,
            handleFrequencyUpdated,
            paymentMethodOptions,
            paymentMethodsLoading,
            selectedPaymentMethodId,
            onPaymentMethodChange,
            showEanInfo,
            customerEanInfo,
            displayEanNumber,
            displayEanContactPerson
        };
    }
});
